import React, { useState, useEffect, use } from 'react';
import { Button, Checkbox, Icon } from '@usitsdasdesign/dds-react';
import Text from 'atoms/Text';
import ButtonGroup from 'molecules/ButtonGroup';
import Drawer from 'molecules/Drawer';
import TimeSheetCalendar from '../organisms/TimeSheetCalendar';
import RenderFields from 'molecules/RenderFields';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupValidator } from 'lib/yup-validator';
import dayjs from 'dayjs';
import SearchableSelect from 'molecules/SearchableSelect';
import { useMemo } from 'react';
import { getShiftTimings } from 'helper/masterDataDropDownHelper';
import useGlobalToastStore from 'store/globalToastStore';
import { customShiftOption, CustomSingleValue, getDemandShiftTimingFormData } from 'helper/shift-timings/helper-function';
import CallSupport from 'organisms/CallSupport';
import CallSupportHour from 'molecules/CallSupportHour';
import AlertModal from 'molecules/AlertModal';

const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function AddShiftDrawerOCS({
  showAddShiftDrawer = false,
  onHideAddShiftDrawer,
  onSubmit,
  startDate,
  endDate,
  // shiftData,
  // setShiftData,
  startDateLabel = "Start date",
  endDateLabel = "End date",
  // onFormSubmit,
  shiftTimings = {},
}) {
  if (!showAddShiftDrawer) return null;
  const isDemandShift = useMemo(() => startDateLabel?.toLowerCase().includes('demand'), [startDateLabel]);
  const [shiftData, setShiftData] = useState(shiftTimings ?? {});
  const [shiftMasterData, setShiftMasterData] = useState({});
  const validationSchema = yup.object().shape({
    shiftStartDate: yup.date().nullable().test(
      'min-date',
      `Start date cannot be before ${dayjs(startDate).format('MM/DD/YYYY')}`,
      (value) => !value || dayjs(value).isSameOrAfter(dayjs(startDate), 'day')
    ),
    shiftEndDate: yup.date().nullable().test(
      'max-date',
      `End date cannot be after ${dayjs(endDate).format('MM/DD/YYYY')}`,
      (value) => !value || dayjs(value).isSameOrBefore(dayjs(endDate), 'day')
    ).test(
      'min-end-date',
      'End date must be after or equal to start date',
      (value, context) => !value || dayjs(value).isSameOrAfter(dayjs(context.parent.shiftStartDate), 'day')
    )
  });
  const [showMissingShiftError, setShowMissingShiftError] = useState(false);
  const defaultValues = useMemo(() => ({
    shiftStartDate: new Date(startDate).toUTCString(),
    shiftEndDate: new Date(endDate).toUTCString(),
    preferences: [{ day: weekdays.slice(0, 5).map(d => ({ label: d, value: d })), time: '' }],
    savePreference: false,
    defaultShiftPreference: null
  }), [startDate, endDate])
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
    watch,
    reset
  } = useForm({
    resolver: yupValidator(validationSchema),
    defaultValues
  });
  const { setToastData } = useGlobalToastStore();

  const clearShiftFilters = () => {
    setValue('shiftStartDate', null);
    setValue('shiftEndDate', null);
    setValue('preferences', [{ day: [], time: '' }]);
  };


  const onFormSubmit = (data) => {
    // if (!validateShiftTimings(shiftData)) {
    //   setToastData({
    //     show: true,
    //     type: 'error',
    //     message: 'Shift should be added to all working days even if the employee is on leave. The applied leave will be auto populated. The missing days are highlighted in red. Please add shift and resubmit',
    //   });
    //   return;
    // }
    onSubmit({ data, shiftData, startDate, endDate });
  };

  const handleAddPreference = () => {
    const updated = [...watch('preferences') ?? []];
    updated.push({ day: [], time: '' });
    setValue('preferences', updated);
  };

  const handlePreferenceChange = (index, field, value) => {
    const updated = [...watch('preferences') ?? []];
    updated[index][field] = value;
    setValue('preferences', updated);
  };

  const handleRemovePreference = (index) => {
    const updated = [...watch('preferences') ?? []];
    updated.splice(index, 1);
    setValue('preferences', updated);
  };
  console.log(watch())

  const handleApplyShiftPreferences = () => {
    const preferences = watch('preferences');
    const shiftStartDate = watch('shiftStartDate');
    const shiftEndDate = watch('shiftEndDate');
    const newShiftData = Object.assign({}, shiftData ?? {});
    let shiftStartDateObject = dayjs(shiftStartDate);
    let showAlert = false;
    while (shiftStartDateObject.isSameOrBefore(dayjs(shiftEndDate), 'day')) {
      const dayLabel = shiftStartDateObject.format('ddd');
      const shiftDateKey = shiftStartDateObject.format('YYYY-MM-DD');
      preferences.forEach((pref) => {
        if (Array.isArray(pref.day)) {
          pref.day.forEach((d) => {
            if (d.value === dayLabel) {
              const onCallSupportDetails ={
                isOnCallSupport:true,
                callSupportType: pref.callSupportType,
                onCallSupportShiftTime: pref.time
              }
              if ( pref?.time?.id === newShiftData[shiftDateKey]?.id) {
                showAlert = true;
                newShiftData[shiftDateKey] = {
                  ...(newShiftData[shiftDateKey] ?? {}),
                  showOnCallConflict: true
                }
              } else {
                newShiftData[shiftDateKey] = {
                  ...(newShiftData[shiftDateKey] ?? {}),
                  showOnCallConflict: false,
                  onCallSupport: onCallSupportDetails
                }
              }
            }
          });
        }
        // else if (pref.day.value === dayLabel) {
        //   newShiftData[shiftDateKey] = pref.time;
        // }
      });
      shiftStartDateObject = shiftStartDateObject.add(1, 'day');
    }
    if (showAlert) {
      setToastData({
        message: `Some shifts overlap with existing ${isDemandShift ? 'demand' : 'allocation'} shifts (highlighted in red). Please choose a different time!`,
        type: 'error',
        show: true
      });
    }
    setShiftData(newShiftData);
  }

  const fields = [
    {
      id: 1,
      type: 'calendor',
      name: 'shiftStartDate',
      label: 'OCS Shift start date',
      isRequired: true,
      size: 'l',
      icon: 'calendar__l__stroke',
      minDate: new Date(startDate),
    },
    {
      id: 2,
      type: 'calendor',
      name: 'shiftEndDate',
      label: 'OCS Shift end date',
      isRequired: true,
      size: 'l',
      icon: 'calendar__l__stroke',
      minDate: new Date(startDate),
      maxDate: new Date(endDate),
    },
  ];

  const buttons = [
    {
      label: 'Cancel',
      kind: 'primary',
      onClick: () => {
        clearShiftFilters();
        setShiftData({});
        onHideAddShiftDrawer();
      },
      size: 'l',
    },
    {
      label: 'Submit',
      kind: 'primaryLoud',
      type: 'submit',
      onClick: () => {
        handleSubmit(onFormSubmit)();
        // onHideAddShiftDrawer();
      },

      size: 'l',
    },
  ];
  const isAllDaysSelectedInPreferences = useMemo(() => {
    const allDaysInPreferences = watch('preferences').reduce((acc, pref) => {
      const allDays = pref.day.map((day) => day.value);
      return acc.concat(allDays);
    }, []);
    return weekdays.every((day) => allDaysInPreferences.includes(day));
  }, [watch('preferences')]);


  const fetchShiftTimings = async () => {
    const response = await getShiftTimings();
    if (response?.ShiftTiming) {
     const parseStartTime = (label) => {
        const match = (label || '').match(/(\d{1,2}):(\d{2})\s?(AM|PM)/i);
        if (!match) return 0;
        let hour = parseInt(match[1], 10);
        const minute = parseInt(match[2], 10);
        const period = match[3].toUpperCase();
        if (period === 'PM' && hour !== 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;
        return hour * 60 + minute;
      };
      const sorted = [...response.ShiftTiming].sort((a, b) => {
        const timeA = parseStartTime(a.label);
        const timeB = parseStartTime(b.label);
        return timeA - timeB;
      });
      setShiftMasterData(sorted);
    }
  };
  useEffect(() => {
    fetchShiftTimings();
  }, []);

  // useEffect(() => {
  //   setShiftData(shiftTimings);
  // }, [shiftTimings, showAddShiftDrawer]);

  useEffect(() => {
    if (showAddShiftDrawer) {
      reset()
    }
  }, [showAddShiftDrawer])

  const [isCallSupportOpen, setIsCallSupportOpen] = useState(false);
  const [activeOnCallDate, setActiveOnCallDate] = useState(null);

  const closeOnCallSupport = () => {
    setActiveOnCallDate(null);
    setIsCallSupportOpen(false);
  }

  const handleCallSupportSubmit = (data) => {
    const onCallSupport = data;
    const addedShift = shiftData[activeOnCallDate] ?? {}
    if (addedShift?.id === data?.onCallSupportShiftTime?.id) {
      setToastData({ message: `This shift overlaps with an existing ${isDemandShift ? 'demand' : 'allocation'} shift. Please choose a different time!`, type: 'error', show: true });
      return;
    }
    const updatedShift = {
      ...addedShift,
      showOnCallConflict: false,
      onCallSupport
    };
    const updatedData = { ...shiftData, [activeOnCallDate]: updatedShift };
    setShiftData(updatedData);
    closeOnCallSupport();
  };

  const onCallSupportDefaultValues = useMemo(() => {
    const defaultValues = {};
    if (!activeOnCallDate || !shiftData) return {}
    const shiftForActiveDate = shiftData[activeOnCallDate]
    if (shiftForActiveDate?.onCallSupport) {
      defaultValues.isOnCallSupport = shiftForActiveDate?.onCallSupport?.isOnCallSupport;
      defaultValues.callSupportType = shiftForActiveDate?.onCallSupport?.callSupportType;
      defaultValues.onCallSupportShiftTime = shiftForActiveDate?.onCallSupport?.onCallSupportShiftTime;

    }
    return defaultValues;
  }, [activeOnCallDate, shiftData])
  console.log(watch());
  const handleCallSupport = (dateStr) => {
    console.log(dateStr)
    setActiveOnCallDate(dateStr);
    setIsCallSupportOpen(true);
  };

  const isApplyDisabled = useMemo(() => {
    const pref = watch('preferences');

    return pref.some((p) => {
      if ((!p?.time?.id || !p?.callSupportType)) {
        return true;
      }
      return false
    })
  }, [watch('preferences')])
  
  return (
    <Drawer
      key={'add-shift-drawer'}
      headerMargin="mb-0"
      show={showAddShiftDrawer}
      width="max-w-[62.5rem]"
      title="Add on-call support shift (OCS shift)"
      headerPadding="px-4 py-4"
      isFooter
      footer={<ButtonGroup buttons={buttons} />}
      onClose={onHideAddShiftDrawer}
      bodyClass="!px-4"
      isPortal={false}
    >
      <div className="flex gap-4 mb-4">
        <div className="flex flex-col gap-[5px] w-[10.75rem]">
          <Text variant="label" color="text-gray-1100">
            {startDateLabel} <span className="text-red-900">*</span>
          </Text>
          <Text color="text-black">{dayjs(startDate).format('MM/DD/YYYY')}</Text>
        </div>
        <div className="flex flex-col gap-[5px] w-[10.75rem]">
          <Text variant="label" color="text-gray-1100">
            {endDateLabel} <span className="text-red-900">*</span>
          </Text>
          <Text color="text-black">{dayjs(endDate).format('MM/DD/YYYY')}</Text>
        </div>
      </div>

      <Text fontSize="text-sm" fontWeight="font-bold" className="mb-2">
        Shift Preferences
      </Text>
      <div className="bg-gray-500 py-4 pl-4 pr-2.5">
        <div className="flex gap-4 mb-4 max-w-[412px]">
          <RenderFields
            fields={fields}
            control={control}
            register={register}
            watch={watch}
            errors={errors}
            setValue={setValue}
            grid="md:grid-cols-2"
            gap="gap-3"
          />
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          {watch('preferences').map((pref, idx) => (
            <div
              className={`relative bg-white py-2 pl-2 ${watch('preferences')?.length > 1 ? 'pr-6' : 'pr-3'
                }  mb-2 w-full lg:w-[calc(50%-12px)]`}
              key={idx}
            >
              <div className='w-full'>
                <div className='grid md:grid-cols-2 gap-4'>
                  <SearchableSelect
                    id={`day-${idx}`}
                    name={`preferences[${idx}].day`}
                    label="OCS Shift days (Repeats on)"
                    placeholder="Select shift days"
                    value={pref.day}
                    isMulti={true}
                    isSelectAll={false}
                    isRequired={true}
                    isDisabled={!watch('shiftStartDate') && !watch('shiftEndDate')}
                    onChange={(e) => handlePreferenceChange(idx, 'day', e)}
                    options={weekdays.filter(weekday => !(watch('preferences').some(pref => pref.day.map(day => day.value).includes(weekday))))
                      .map((opt) => ({ label: opt, value: opt }))}
                    control={control}
                    register={register}
                    color={'#489A32'}
                  />
                  <SearchableSelect
                    id={`time-${idx}`}
                    className='time-custom-select'
                    name={`preferences[${idx}].time`}
                    label="OCS Shift time"
                    placeholder="Select Shift timings"
                    isRequired={true}
                    value={pref.time}
                    isDisabled={!watch('preferences')[idx]?.day?.length || shiftMasterData?.length === 0 || !watch('preferences')[idx]}
                    onChange={(e) => handlePreferenceChange(idx, 'time', e)}
                    options={shiftMasterData}
                    control={control}
                    register={register}
                    ignoreTextColor={true}
                    components={{ Option: customShiftOption, SingleValue: CustomSingleValue }}
                  />
                </div>
                <CallSupportHour
                  className="mt-4"
                  register={register}
                  control={control}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  baseName={`preferences[${idx}]`}
                />
              </div>
              {watch('preferences').length > 1 && (
                <span className="absolute right-3 top-2" onClick={() => handleRemovePreference(idx)}>
                  <Icon
                    color="#A3A3A3"
                    className="cursor-pointer w-2.5"
                    icon="delete__s__stroke"
                    size="s"
                  />
                </span>
              )}
            </div>
          ))}
          {isAllDaysSelectedInPreferences ? <></> : <Button
            kind="primary"
            size="m"
            color="green"
            label="Add"
            icon="plus__s__stroke"
            onClick={handleAddPreference}
            className=''
          />}
        </div>
      </div>

      <div className="flex flex-wrap justify-between mt-2 mb-4 gap-4">
        {/* <Checkbox
          label="Mark this as a default shift preference"
          value={watch('savePreference')}
          name="savePreference"
          className="small"
          showLabel
          onChangeHandler={(checked) => setValue('savePreference', checked.value)}
        /> */}
        <div className="flex gap-2 ml-auto">
          <Button
            kind="silent"
            label="Reset"
            size="l"
            className="!justify-center"
            onClick={() => { clearShiftFilters(); }}
          />
          <Button
            kind="primaryLoud"
            label="Apply"
            size="l"
            className="!justify-center"
            onClick={handleApplyShiftPreferences}
            isDisabled={watch('preferences')?.some((p) => {
              if ((!p?.time?.id || !p?.callSupportType)) {
                return true;
              }
              return false
            })}
          />
        </div>
      </div>

      <TimeSheetCalendar
        showMissingShiftError={showMissingShiftError}
        shiftMasterData={shiftMasterData}
        shiftData={shiftData}
        setShiftData={setShiftData}
        startDate={startDate}
        endDate={endDate}
        isCallSupport={true}
        handleCallSupport={handleCallSupport}
        readOnly={true}
      />
      <AlertModal
        show={isCallSupportOpen}
        isShowSave={false}
        showCalcelButton={false}
        title="on-call support"
        description=""
        onClose={closeOnCallSupport}
        isFooter={false}
        saveLabel="Save"
        cancelLabel="Clear All"
        cancelLabelVariant='silent'
        cancelLabelColor="green"
        customSize="!max-w-[280px]"
      >
        <CallSupport
          defaultValues={onCallSupportDefaultValues}
          handleCallSupportSubmit={handleCallSupportSubmit}
          className='pt-2 '
          shiftMasterData={shiftMasterData}
          onClose={closeOnCallSupport}
        />
      </AlertModal>
    </Drawer>
  );
}

export default AddShiftDrawerOCS;
